import { useEffect } from 'react';
import { Box, Button, Link, Paper, Stack, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { Sentry } from '../sentry';
import wafLogoUrl from '../assets/waf-logo.png';

const SUPPORT_EMAIL = 'waf.customercare@emap.com';

/**
 * Shown when a user is fully authenticated but holds no role the portal
 * recognises — so there's no area we can route them to. Without this they'd
 * land in an invisible redirect loop with no explanation. We also fire a Sentry
 * event so we hear about it before the user has to report a vague "it keeps
 * sending me back to login".
 */
export function NoAccess() {
  const { user, logout } = useAuth();

  useEffect(() => {
    Sentry.captureMessage('Authenticated user has no usable portal role', {
      level: 'warning',
      tags: { area: 'auth', subject: user?.userId ?? 'unknown' },
      // email kept out of tags (PII in aggregations) but available on the event.
      extra: { email: user?.email, roles: user?.roles ?? [] },
    });
    // Once per signed-in identity reaching this dead end.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId]);

  return (
    <Box sx={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Paper sx={{ p: { xs: 3, sm: 4 }, maxWidth: 480, width: '100%', textAlign: 'center' }}>
        <Stack spacing={2} alignItems="center">
          <Box component="img" src={wafLogoUrl} alt="" sx={{ height: 44, maxWidth: '100%' }} />
          <Typography variant="h5">No portal access</Typography>
          <Typography variant="body2" color="text.secondary">
            You&rsquo;re signed in{user?.email ? <> as <strong>{user.email}</strong></> : ''}, but your
            account doesn&rsquo;t have access to any area of the portal yet.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please contact <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link> and we&rsquo;ll
            get it sorted. It helps if you mention the email address above.
          </Typography>
          <Button variant="outlined" onClick={() => logout()} sx={{ mt: 1 }}>
            Sign out
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
